import { Poll } from 'schema/poll';
import { ICState } from 'schema/state';
import { AllEntries, AllKeys } from './advancedTypes';
import { DEBUG_ID } from './constants';

declare global {
  type APPEARANCE = 'light' | 'dark';

  const _APP_EXTERNAL_LOGIC_RULES_API_URL: string | undefined;
  const _APP_POLL_CONTEXT_URL: string | undefined;
  const _APP_FILE_UPLOAD_V1_URL: string | undefined;
  const _APP_FILE_UPLOAD_V2_URL: string | undefined;
  const _APP_IC_CONTEXT_URL: string | undefined;
  const _APP_POLL_SERVER_REDUCER: string | undefined;

  const _APP_TEST_AUTH_DISABLED: boolean | undefined;
  const _APP_TWORK_REDIRECT_URL: string | undefined;
  const _APP_TWORK_LOGIN_URL: string | undefined;
  const _APP_TWORK_CLIENT_ID: string | undefined;
  const _POLL_STORAGE_UPDATE_TIMEOUT: string | undefined;
  const _POLL_ERROR_TEXT: string | undefined;
  const _POLL_ERROR_URL: string | undefined;
  const _APP_SENTRY_DSN: string | undefined;

  const _APP_DYNAMIC_CONFIG_URL: string | undefined;
  const _APP_DYNAMIC_CONFIG_FALLBACK_URL: string | undefined;
  const _APP_DYNAMIC_CONFIG_TIMEOUT_MS: string | undefined;

  function c(className: string | undefined): string;

  interface Window {
    FORCE_IFRAME_MODE: boolean | undefined;
    FORCE_WEBVIEW_MODE: boolean | undefined;
    POLL_MIN_HEIGHT: number | undefined;

    __pollData: Poll | undefined;
    __pollComputedLogicRules: ICState['externalLogicRules'] | undefined;
    __internals: any;
    __title: string | undefined;
    __pollsImageDomain?: string;
    APP_EXTERNAL_LOGIC_RULES_API_URL: string;
    APP_POLL_CONTEXT_URL: string;
    APP_FILE_UPLOAD_V1_URL: string;
    APP_FILE_UPLOAD_V2_URL: string;
    APP_IC_CONTEXT_URL: string;
    APP_POLL_SERVER_REDUCER: string;

    APP_TEST_AUTH_DISABLED: boolean;
    APP_TWORK_REDIRECT_URL: string;
    APP_TWORK_LOGIN_URL: string;
    APP_TWORK_CLIENT_ID: string;
    POLL_STORAGE_UPDATE_TIMEOUT: string;
    POLL_ERROR_TEXT: string;
    POLL_ERROR_URL: string;
    APP_SENTRY_DSN: string | undefined;

    /** По тестированию и работе динамического конфигурирования есть раздел в README */
    APP_DYNAMIC_CONFIG_URL: string;
    APP_DYNAMIC_CONFIG_FALLBACK_URL: string;
    APP_DYNAMIC_CONFIG_TIMEOUT_MS: number;

    _APP_EXTERNAL_LOGIC_RULES_API_URL: string;
    _APP_POLL_CONTEXT_URL: string;
    _APP_FILE_UPLOAD_V1_URL: string;
    _APP_FILE_UPLOAD_V2_URL: string;
    _APP_IC_CONTEXT_URL: string;
    _APP_POLL_SERVER_REDUCER: string;

    _APP_DYNAMIC_CONFIG_URL: string;
    _APP_DYNAMIC_CONFIG_FALLBACK_URL: string;
    _APP_DYNAMIC_CONFIG_TIMEOUT_MS: string;

    _APP_TEST_AUTH_DISABLED: boolean;
    _APP_TWORK_REDIRECT_URL: string;
    _APP_TWORK_LOGIN_URL: string;
    _APP_TWORK_CLIENT_ID: string;
    _POLL_STORAGE_UPDATE_TIMEOUT: string;
    _POLL_ERROR_TEXT: string;
    _POLL_ERROR_URL: string;
    _APP_SENTRY_DSN: string | undefined;
  }
  interface ObjectConstructor {
    keys<T extends object>(o: T): AllKeys<T>[];
    entries<T extends Record<string, unknown>>(o: T): AllEntries<T>[];
  }
}

type ENV = {
  [P in WithoutUnderscore<Extract<keyof Window, `_APP_${string}` | `_POLL_${string}` | `_DEBUG_${string}`>>]:
    | Window[P]
    | undefined;
};
type WithoutUnderscore<T extends string> = T extends T ? (T extends `_${infer R}` ? R : T) : T;

export function setGlobalEnv(env: ENV) {
  window.APP_EXTERNAL_LOGIC_RULES_API_URL =
    env.APP_EXTERNAL_LOGIC_RULES_API_URL?.trim() ||
    window._APP_EXTERNAL_LOGIC_RULES_API_URL?.trim() ||
    window.APP_EXTERNAL_LOGIC_RULES_API_URL;
  window.APP_TEST_AUTH_DISABLED =
    env.APP_TEST_AUTH_DISABLED ?? window._APP_TEST_AUTH_DISABLED ?? window.APP_TEST_AUTH_DISABLED;
  window.APP_TWORK_LOGIN_URL =
    env.APP_TWORK_LOGIN_URL?.trim() || window._APP_TWORK_LOGIN_URL?.trim() || window.APP_TWORK_LOGIN_URL;
  window.APP_TWORK_CLIENT_ID =
    env.APP_TWORK_CLIENT_ID?.trim() || window._APP_TWORK_CLIENT_ID?.trim() || window.APP_TWORK_CLIENT_ID;
  window.APP_TWORK_REDIRECT_URL =
    env.APP_TWORK_REDIRECT_URL?.trim() || window._APP_TWORK_REDIRECT_URL?.trim() || window.APP_TWORK_REDIRECT_URL;
  window.APP_POLL_CONTEXT_URL =
    env.APP_POLL_CONTEXT_URL?.trim() || window._APP_POLL_CONTEXT_URL?.trim() || window.APP_POLL_CONTEXT_URL;
  window.APP_FILE_UPLOAD_V1_URL =
    env.APP_FILE_UPLOAD_V1_URL?.trim() || window._APP_FILE_UPLOAD_V1_URL?.trim() || window.APP_FILE_UPLOAD_V1_URL;
  window.APP_FILE_UPLOAD_V2_URL =
    env.APP_FILE_UPLOAD_V2_URL?.trim() || window._APP_FILE_UPLOAD_V2_URL?.trim() || window.APP_FILE_UPLOAD_V2_URL;
  window.APP_IC_CONTEXT_URL =
    env.APP_IC_CONTEXT_URL?.trim() || window._APP_IC_CONTEXT_URL?.trim() || window.APP_IC_CONTEXT_URL;
  window.APP_POLL_SERVER_REDUCER =
    env.APP_POLL_SERVER_REDUCER?.trim() || window._APP_POLL_SERVER_REDUCER?.trim() || window.APP_POLL_SERVER_REDUCER;
  window.POLL_STORAGE_UPDATE_TIMEOUT =
    env.POLL_STORAGE_UPDATE_TIMEOUT?.trim() ||
    window._POLL_STORAGE_UPDATE_TIMEOUT?.trim() ||
    window.POLL_STORAGE_UPDATE_TIMEOUT;
  window.POLL_ERROR_URL = env.POLL_ERROR_URL?.trim() || window._POLL_ERROR_URL?.trim() || window.POLL_ERROR_URL;
  window.POLL_ERROR_TEXT = env.POLL_ERROR_TEXT?.trim() || window._POLL_ERROR_TEXT?.trim() || window.POLL_ERROR_TEXT;
  window.APP_SENTRY_DSN = env.APP_SENTRY_DSN?.trim() || window._APP_SENTRY_DSN?.trim() || window.APP_SENTRY_DSN;

  window.APP_DYNAMIC_CONFIG_URL =
    env.APP_DYNAMIC_CONFIG_URL?.trim() || window._APP_DYNAMIC_CONFIG_URL?.trim() || window.APP_DYNAMIC_CONFIG_URL;
  window.APP_DYNAMIC_CONFIG_FALLBACK_URL =
    env.APP_DYNAMIC_CONFIG_FALLBACK_URL?.trim() ||
    window._APP_DYNAMIC_CONFIG_FALLBACK_URL?.trim() ||
    window.APP_DYNAMIC_CONFIG_FALLBACK_URL;
}

setGlobalEnv({
  APP_EXTERNAL_LOGIC_RULES_API_URL: _APP_EXTERNAL_LOGIC_RULES_API_URL,
  APP_POLL_CONTEXT_URL: _APP_POLL_CONTEXT_URL,
  APP_FILE_UPLOAD_V1_URL: _APP_FILE_UPLOAD_V1_URL,
  APP_FILE_UPLOAD_V2_URL: _APP_FILE_UPLOAD_V2_URL,
  APP_IC_CONTEXT_URL: _APP_IC_CONTEXT_URL,
  APP_POLL_SERVER_REDUCER: _APP_POLL_SERVER_REDUCER,

  APP_TEST_AUTH_DISABLED: _APP_TEST_AUTH_DISABLED,
  APP_TWORK_REDIRECT_URL: _APP_TWORK_REDIRECT_URL,
  APP_TWORK_LOGIN_URL: _APP_TWORK_LOGIN_URL,
  APP_TWORK_CLIENT_ID: _APP_TWORK_CLIENT_ID,
  POLL_STORAGE_UPDATE_TIMEOUT: _POLL_STORAGE_UPDATE_TIMEOUT,
  POLL_ERROR_TEXT: _POLL_ERROR_TEXT,
  POLL_ERROR_URL: _POLL_ERROR_URL,
  APP_SENTRY_DSN: _APP_SENTRY_DSN,

  APP_DYNAMIC_CONFIG_FALLBACK_URL: _APP_DYNAMIC_CONFIG_FALLBACK_URL,
  APP_DYNAMIC_CONFIG_URL: _APP_DYNAMIC_CONFIG_URL,
  APP_DYNAMIC_CONFIG_TIMEOUT_MS: toNumberOr(_APP_DYNAMIC_CONFIG_TIMEOUT_MS, 30000),
});

function toNumberOr(v: unknown, def: number): number {
  const res = Number(v);
  return isNaN(res) ? def : res;
}

window.Prism = window.Prism || {};
window.Prism.manual = true;
window.Prism.disableWorkerMessageHandler = true;

if (process.env.NODE_ENV === 'development') {
  // Добавляет debug возможности для опросов в WebView

  (function () {
    // eslint-disable-next-line no-console
    const consoleTrace = console.trace;
    const stringify = (it: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      return it != null ? (typeof it === 'object' ? JSON.stringify(it) : it.toString()) : '';
    };
    // eslint-disable-next-line no-console
    console.trace = function (...args) {
      const debug = document.getElementById(DEBUG_ID);
      if (debug) debug.appendChild(document.createTextNode(stringify(args[0]) + '\n'));
      consoleTrace.apply(console, args);
    };
  })();
}

window.c = className => {
  if (!className) {
    const errorMessage = `Classname is undefined!`;
    console.error(errorMessage);

    if (process.env.NODE_ENV === 'development') {
      throw new Error(errorMessage);
    }

    return '';
  }

  return className;
};
